import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../utils';
import { useAuth } from './AuthContext';

export const OrgContext = createContext(null);

/**
 * Decode a JWT payload without verifying the signature. We only use this to
 * surface the server-issued `role` + `active_org_id` claims into React state —
 * authority for anything sensitive still lives with the backend.
 */
function decodeJwt(token) {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padLen = padded.length % 4;
    const withPad = padLen ? padded + '='.repeat(4 - padLen) : padded;
    const json = atob(withPad);
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}

/**
 * Backend returns integrations as a list of rows (one per provider). The UI
 * indexes by provider name for chip lookup, so normalize once at the boundary.
 */
function normalizeOrg(org) {
  const integrations = {};
  for (const row of org.integrations || []) {
    if (row.provider) integrations[row.provider] = row;
  }
  return { ...org, integrations };
}

// Key under which the super-admin's own token is stashed while impersonating, so
// "Exit impersonation" can restore the original session.
const IMPERSONATOR_KEY = 'impersonator_token';

function readClaimsFromStorage() {
  const token = localStorage.getItem('token');
  const payload = decodeJwt(token);
  if (!payload) return { role: null, activeOrgId: null, impersonatedBy: null };
  return {
    role: payload.role || null,
    activeOrgId: payload.active_org_id || null,
    impersonatedBy: payload.impersonated_by || null,
  };
}

export const OrgProvider = ({ children }) => {
  const { user, refreshProfile, isSuperAdmin } = useAuth();
  const [role, setRole] = useState(() => readClaimsFromStorage().role);
  const [activeOrgId, setActiveOrgId] = useState(() => readClaimsFromStorage().activeOrgId);
  const [impersonatedBy, setImpersonatedBy] = useState(() => readClaimsFromStorage().impersonatedBy);
  const [orgs, setOrgs] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [error, setError] = useState(null);

  // Whenever the underlying token changes (login/logout/switch), re-read claims.
  useEffect(() => {
    const { role: r, activeOrgId: oid, impersonatedBy: imp } = readClaimsFromStorage();
    setRole(r);
    setActiveOrgId(oid);
    setImpersonatedBy(imp);
  }, [user]);

  const refreshOrgs = useCallback(async () => {
    if (!localStorage.getItem('token')) {
      setOrgs([]);
      return;
    }
    try {
      setLoadingOrgs(true);
      setError(null);
      // Platform admins (super_admin — incl. an account admin who also holds the
      // platform super_admin designation) get the full tenant list with account
      // name, plan and integration status; everyone else gets the orgs they can
      // switch into within their business account.
      const url = (isSuperAdmin || role === 'super_admin')
        ? `${API_BASE_URL}/api/organizations`
        : `${API_BASE_URL}/api/organizations/switchable`;
      const resp = await axios.get(url);
      setOrgs((resp.data?.organizations || []).map(normalizeOrg));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load organizations');
    } finally {
      setLoadingOrgs(false);
    }
  }, [role, isSuperAdmin]);

  // Tracks the (role, activeOrgId) we last auto-loaded for. Guards against React
  // 18 StrictMode double-invoking this effect in dev — without it the org list is
  // fetched twice on mount. Manual refreshOrgs() calls bypass this and always hit
  // the network.
  const lastAutoLoadKey = useRef(null);

  // Load the user's orgs once a token + role are known.
  useEffect(() => {
    if (!role) {
      lastAutoLoadKey.current = null;
      setOrgs([]);
      return;
    }
    const key = `${role}:${isSuperAdmin}:${activeOrgId}`;
    if (lastAutoLoadKey.current === key) return;
    lastAutoLoadKey.current = key;
    refreshOrgs();
  }, [role, isSuperAdmin, activeOrgId, refreshOrgs]);

  const applyNewToken = useCallback((token) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    const payload = decodeJwt(token);
    setRole(payload?.role || null);
    setActiveOrgId(payload?.active_org_id || null);
  }, []);

  const switchOrg = useCallback(async (orgId) => {
    const resp = await axios.post(`${API_BASE_URL}/api/organizations/switch/${orgId}`);
    if (resp.data?.access_token) {
      applyNewToken(resp.data.access_token);
      // Permissions and roles are org-scoped — refresh /me so hasPermission and
      // the nav reflect the newly active org.
      if (refreshProfile) await refreshProfile();
      return resp.data;
    }
    throw new Error('Switch response missing access_token');
  }, [applyNewToken, refreshProfile]);

  const createOrg = useCallback(async (payload) => {
    const resp = await axios.post(`${API_BASE_URL}/api/organizations`, payload);
    // Backend auto-switches into the new org and returns a fresh token.
    if (resp.data?.access_token) {
      applyNewToken(resp.data.access_token);
      if (refreshProfile) await refreshProfile();
    }
    await refreshOrgs();
    return resp.data;
  }, [applyNewToken, refreshOrgs, refreshProfile]);

  const updateOrg = useCallback(async (orgId, payload) => {
    const resp = await axios.patch(`${API_BASE_URL}/api/organizations/${orgId}`, payload);
    await refreshOrgs();
    return resp.data;
  }, [refreshOrgs]);

  const retryProvisioning = useCallback(async (orgId, provider) => {
    const resp = await axios.post(
      `${API_BASE_URL}/api/organizations/${orgId}/integrations/${provider}/provision`
    );
    await refreshOrgs();
    return resp.data;
  }, [refreshOrgs]);

  // Suspend / reactivate a tenant (super admin). Returns the updated org.
  const setOrgStatus = useCallback(async (orgId, action) => {
    const resp = await axios.post(`${API_BASE_URL}/api/organizations/${orgId}/${action}`);
    await refreshOrgs();
    return resp.data;
  }, [refreshOrgs]);

  // Impersonate a user inside an org. Stashes the super-admin token so the
  // session can be restored, swaps in the scoped token, and hard-reloads so the
  // whole app (AuthContext /me, OrgContext claims) re-bootstraps as that user.
  const impersonate = useCallback(async (orgId, userId) => {
    const current = localStorage.getItem('token');
    const resp = await axios.post(
      `${API_BASE_URL}/api/organizations/${orgId}/impersonate`,
      userId ? { user_id: userId } : {}
    );
    if (!resp.data?.access_token) {
      throw new Error('Impersonation response missing access_token');
    }
    if (current) localStorage.setItem(IMPERSONATOR_KEY, current);
    localStorage.setItem('token', resp.data.access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${resp.data.access_token}`;
    window.location.href = '/';
    return resp.data;
  }, []);

  // Restore the stashed super-admin session and return to the admin panel.
  const exitImpersonation = useCallback(() => {
    const original = localStorage.getItem(IMPERSONATOR_KEY);
    if (original) {
      localStorage.setItem('token', original);
      localStorage.removeItem(IMPERSONATOR_KEY);
      axios.defaults.headers.common['Authorization'] = `Bearer ${original}`;
    }
    window.location.href = '/admin-panel';
  }, []);

  const activeOrg = orgs.find((o) => String(o.id) === String(activeOrgId)) || null;

  return (
    <OrgContext.Provider
      value={{
        role,
        activeOrgId,
        activeOrg,
        orgs,
        loadingOrgs,
        error,
        refreshOrgs,
        switchOrg,
        createOrg,
        updateOrg,
        retryProvisioning,
        setOrgStatus,
        impersonate,
        exitImpersonation,
        impersonatedBy,
        isImpersonating: !!impersonatedBy || !!localStorage.getItem(IMPERSONATOR_KEY),
        applyNewToken,
        isSuperAdmin: role === 'super_admin',
      }}
    >
      {children}
    </OrgContext.Provider>
  );
};

export const useActiveOrg = () => {
  const ctx = useContext(OrgContext);
  if (!ctx) {
    throw new Error('useActiveOrg must be used within an OrgProvider');
  }
  return ctx;
};
